import { useHistory, useParams } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import { OverlayPanel } from "primereact/overlaypanel";
import useTranslations from "../../../core/i18n/useTranslations";
import { useSaveObject } from "../../../core/SaveObjectContext/hooks";
import Button from "../../../ui-lib/components/Button/Button";
import { PopupContextMenuLinkButton } from "../../../ui-lib/components/Menus/PopupContextMenu/PopupContextMenuLinkButton";
import { DeleteAdministratorModal } from "./DeleteAdministratorModal";
import {
  makeCentralAdmin,
  makeOperator,
  requestDeviceLog,
} from "../../../core/api/administrators/administrators";
import { getSellers } from "../../../core/api/sellers/sellers";
import { notify } from "../../../ui-lib/components/Alerts/Toast";
import { notifyApiErrors } from "../../../core/helpers/helpers";
import useUser from "../../../core/user/useUser";
import RequestLogModal from "../../Organizations/RequestOrganizationLogModal";
import { CopyAdministratorModal } from "./CopyAdministrator/CopyAdministratorModal";
import { AdministratorDetails } from "../../../core/api/administrators/types";
import { ImpersonateModal } from "./ImpersonateModal";
import useSharedAdministrator from "./SharedAdministratorContext/useSharedAdministrator";
import PrimeModal from "../../../ui-lib/components/PrimeModal/PrimeModal";
import { IItemProp } from "../../../ui-lib/components/Dropdown/DropdownItem";

const AdministratorActions = ({
  administratorData,
  identity,
}: {
  administratorData?: AdministratorDetails;
  identity: boolean;
}) => {
  const t = useTranslations();
  const history = useHistory();
  const { canSave, isDirty, isSaving, onSave } = useSaveObject();
  const { reload } = useSharedAdministrator();
  const { authenticatedRequest, accountId, config } = useUser();
  const { id: adminId } = useParams<{ id: string }>();

  const contextMenuRef = useRef<OverlayPanel>(null);

  const viserPermission = config?.show.includes("SensioHideFields");

  const [sellers, setSellers] = useState<IItemProp[]>([]);
  const [requestLogModalOpen, setRequestLogModalOpen] = useState(false);
  const [copyModalOpen, setCopyModalOpen] = useState(false);
  const [deleteAdminModalOpen, setDeleteAdminModalOpen] = useState(false);
  const [impersonateModalOpen, setImpersonateModalOpen] = useState(false);

  const fetchSellers = async () => {
    try {
      const res = await getSellers(authenticatedRequest);
      if (res.data) {
        const sellersItems = res.data.map(
          (seller: any) =>
            ({
              id: seller.id,
              name: seller.name,
              isSelected: false,
            } as IItemProp)
        );
        setSellers(sellersItems);
      }
    } catch (error: any) {
      notifyApiErrors(error.response?.data?.errors);
    }
  };

  const changeAdminType = async (respondentToAdmin = true) => {
    try {
      respondentToAdmin
        ? await makeCentralAdmin(administratorData?.id!, authenticatedRequest)
        : await makeOperator(administratorData?.id!, authenticatedRequest);
      await reload();
      notify({
        message: t(
          `Administrator:to_${
            respondentToAdmin ? "admin" : "respondent"
          }_success`
        ),
      });
    } catch (error: any) {
      notifyApiErrors(error.response?.data?.errors);
    } finally {
      contextMenuRef.current?.hide();
    }
  };

  useEffect(() => {
    if (administratorData?.type === "seller") {
      fetchSellers();
    }
  }, []);

  return (
    <>
      <Button
        text={t("Common:save")}
        disabled={!isDirty || !canSave}
        loading={isSaving}
        onClick={onSave}
      />
      <Button
        className="ml-8"
        type="button"
        variant="secondary"
        image="dots-horizontal"
        text={t("Common:other")}
        onClick={(e) => contextMenuRef.current?.toggle(e)}
      />
      <OverlayPanel ref={contextMenuRef} className="actions-overlay">
        {!viserPermission && (
          <PopupContextMenuLinkButton
            icon="cloud-download"
            text={t("Objects:request_logs")}
            onClick={async () => {
              try {
                await requestDeviceLog(adminId, authenticatedRequest);
                notify({
                  message: t("Objects:request_successfully_send"),
                });
              } catch (error: any) {
                notifyApiErrors(
                  error.response?.data?.errors,
                  error.response?.status
                );
              } finally {
                contextMenuRef.current?.hide();
              }
            }}
          />
        )}

        {administratorData?.type === "respondent" && !viserPermission && (
          <PopupContextMenuLinkButton
            icon="status-online"
            text={t("Administrator:operator_alarms")}
            onClick={async () => {
              const id = administratorData?.id;
              history.push({
                pathname: "/adminportal/alarms",
                search: `?searchType=-1&page=1&pageSize=100&filterType=7&filterValue=${id}`,
              });
            }}
          />
        )}

        {config?.show.includes("NewAdminBeta") &&
          config?.show.includes("ChangeAdminsMakeCenterAdmin") &&
          (administratorData?.type === "respondent" ||
            administratorData?.type === "responscenter") && (
            <PopupContextMenuLinkButton
              text={t(`Administrator:from_${administratorData?.type}_convert`)}
              icon={
                administratorData?.type === "respondent"
                  ? "arrow-up"
                  : "arrow-down"
              }
              onClick={() => {
                changeAdminType(administratorData?.type === "respondent");
              }}
            />
          )}

        {config?.show.includes("DownloadAdminEvents") && (
          <PopupContextMenuLinkButton
            icon="download"
            text={t("Administrator:request_log")}
            onClick={() => {
              setRequestLogModalOpen(true);
              contextMenuRef.current?.hide();
            }}
          />
        )}

        {administratorData?.type !== "responscenter" &&
          config?.show.includes("CreateAdminByCopy") && (
            <PopupContextMenuLinkButton
              icon="document-add"
              text={t("Administrator:copy_admin_menu")}
              onClick={() => {
                setCopyModalOpen(true);
                contextMenuRef.current?.hide();
              }}
            />
          )}

        {administratorData?.canBeImpersonated && (
          <>
            <PopupContextMenuLinkButton
              icon="switch-horizontal"
              text={t("Administrator:impersonate")}
              onClick={() => setImpersonateModalOpen(true)}
            />
            <ImpersonateModal
              isOpen={impersonateModalOpen}
              adminData={administratorData}
              onClose={() => setImpersonateModalOpen(false)}
            />
          </>
        )}

        {accountId !== administratorData?.id && (
          <PopupContextMenuLinkButton
            icon="trash"
            text={t("Administrator:delete")}
            onClick={() => setDeleteAdminModalOpen(true)}
          />
        )}
      </OverlayPanel>
      <PrimeModal
        withHeader
        fixedWidth="576px"
        header={t("Administrator:request_log")}
        isOpen={requestLogModalOpen}
        onClose={() => setRequestLogModalOpen(false)}
      >
        <RequestLogModal
          onClose={() => setRequestLogModalOpen(false)}
          logTarget="Admin"
        />
      </PrimeModal>
      {copyModalOpen && (
        <CopyAdministratorModal
          copyModalOpen={copyModalOpen}
          onClose={() => setCopyModalOpen(false)}
          adminData={administratorData!}
          sellers={sellers}
        />
      )}
      <DeleteAdministratorModal
        isOpen={deleteAdminModalOpen}
        administratorId={administratorData?.id}
        identity={identity}
        onDelete={() => history.push("/adminportal/administrators")}
        onClose={() => {
          setDeleteAdminModalOpen(false);
        }}
      />
    </>
  );
};

export default AdministratorActions;
